import { FC, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Loader,
  Svg,
  Typography,
  useThemeValue,
} from '@altermaind/web/ui';
import { RenderLocalesWithComponents } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  Steps,
  getSurveyCampaignInvitationByTemplateIdCall,
  getSurveysCampaignsCall,
  getTemplatesCall,
} from '../../state';

type Props = {
  setStep: (step: Steps) => void;
  setSelectedUsersIds: (ids: string[]) => void;
  selectedUsersIds?: string[];
  selectedDepartments: string[];
  setSelectedDepartments: (depts: string[]) => void;
  currentTemplateId?: string;
  currentVersionId?: number;
  currentCampaignId?: string;
  selectedTemplate?: string;
};

export const StepRecapDepartments: FC<Props> = ({
  setStep,
  setSelectedUsersIds,
  selectedUsersIds,
  selectedDepartments,
  setSelectedDepartments,
  currentTemplateId,
  currentVersionId,
  currentCampaignId,
  selectedTemplate,
}) => {
  const t = useTranslations('surveys.sendSurvey.stepRecapDepartments');
  const tShared = useTranslations();
  const unknownDeptLabel = t('unknownDepartment');
  const { genericResponsiveValue } = useResponsiveStyle();

  const gapXs = useThemeValue('space', 'xs');
  const message = tShared('errors.genericApiError');

  const { surveyId } = useParams();

  const {
    departments,
    execute: executeGetSurveyCampaignInvitationByTemplateId,
    loading: loadingGetSurveyCampaignInvitationByTemplateId,
    success: successGetSurveyCampaignInvitationByTemplateId,
    error: errorGetSurveyCampaignInvitationByTemplateId,
  } = getSurveyCampaignInvitationByTemplateIdCall.hook();

  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  const { response: responseGetTemplates } = getTemplatesCall.hook();

  /**
   * Smart-diff employee selection.
   * `selectedUsersIdsRef` reads current value inside the effect without being a dep.
   * `prevSelectedDepartmentsRef` starts as `undefined` (sentinel for initial mount).
   *
   * Rules:
   * - Initial mount + pre-selected depts + no existing selection → populate all dept employees.
   * - Dept added → add its employees (skip duplicates).
   * - Dept removed → remove its employees; individual deselections from StepListEmployees
   *   are preserved because the purge is based on dept data, not on selectedUsersIds.
   */
  const selectedUsersIdsRef = useRef<string[]>(selectedUsersIds ?? []);
  selectedUsersIdsRef.current = selectedUsersIds ?? [];

  const prevSelectedDepartmentsRef = useRef<string[] | undefined>(undefined);

  useEffect(() => {
    if (!departments) {
      return;
    }

    const prevDepts = prevSelectedDepartmentsRef.current;
    prevSelectedDepartmentsRef.current = selectedDepartments;

    // Initial mount: populate only when no current selections but departments are pre-selected
    if (prevDepts === undefined) {
      if (selectedUsersIdsRef.current.length > 0 || selectedDepartments.length === 0) return;
      const allEmails = departments
        .filter(d => d.name && selectedDepartments.includes(d.name))
        .flatMap(d => d.employees?.map(e => e.email ?? '') ?? [])
        .filter(Boolean);
      setSelectedUsersIds(allEmails);
      return;
    }

    const addedDepts = selectedDepartments.filter(d => !prevDepts.includes(d));
    const removedDepts = prevDepts.filter(d => !selectedDepartments.includes(d));

    if (addedDepts.length === 0 && removedDepts.length === 0) return;

    const removedEmails = new Set(
      departments
        .filter(d => d.name && removedDepts.includes(d.name))
        .flatMap(d => d.employees?.map(e => e.email ?? '') ?? []),
    );

    const addedEmails = departments
      .filter(d => d.name && addedDepts.includes(d.name))
      .flatMap(d => d.employees?.map(e => e.email ?? '') ?? [])
      .filter(Boolean);

    const { current } = selectedUsersIdsRef;
    setSelectedUsersIds([
      ...current.filter(id => !removedEmails.has(id)),
      ...addedEmails.filter(id => !current.includes(id)),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartments, departments, setSelectedUsersIds]);

  useEffect(() => {
    const internalSurveyId = selectedTemplate
      ? responseGetTemplates?.find(template => template.name === selectedTemplate)?.id
      : surveyId;
    const versions = responseGetTemplates?.find(
      template => template.id === internalSurveyId,
    )?.versions;
    const lastTemplateVersion = versions ? versions[versions.length - 1] : undefined;

    const internalCampaignId = selectedTemplate
      ? responseGetSurveysCampaigns?.findLast(
          campaign => campaign.surveyTemplateId === internalSurveyId,
        )?.id
      : currentCampaignId;

    if (internalSurveyId && lastTemplateVersion && lastTemplateVersion.version) {
      executeGetSurveyCampaignInvitationByTemplateId({
        campaignId: internalCampaignId || undefined,
        templateId: currentTemplateId || (internalSurveyId as string),
        versionId: currentVersionId?.toString() || lastTemplateVersion.version?.toString(),
      });
    }
  }, [
    currentCampaignId,
    currentTemplateId,
    currentVersionId,
    executeGetSurveyCampaignInvitationByTemplateId,
    responseGetSurveysCampaigns,
    responseGetTemplates,
    selectedTemplate,
    surveyId,
  ]);

  return (
    <Box mb='xs' mt='m' width='100%'>
      <Typography textAlign='center' variant='p2/light'>
        {t('description')}
      </Typography>
      {loadingGetSurveyCampaignInvitationByTemplateId && (
        <Box mt='l' textAlign='center'>
          <Loader size='s' />
        </Box>
      )}
      {errorGetSurveyCampaignInvitationByTemplateId && (
        <AlertBadge containerProps={{ mt: 'l' }} iconName='info' type='error' label={message} />
      )}
      {successGetSurveyCampaignInvitationByTemplateId && (
        <Box
          display='grid'
          gridTemplateColumns={genericResponsiveValue({
            breakPoints: { desktop: '1fr 1fr' },
            defaultValue: 'repeat(2fr, 1fr)',
          })}
          style={{ gap: gapXs }}
          mt='m'
        >
          {departments &&
            departments.map(dep => {
              const department = {
                ...dep,
                teams: [...(dep.teams?.filter(team => team !== 'unknown') || [])],
                displayName: dep.name?.toLowerCase() === 'unknown' ? unknownDeptLabel : dep.name,
              };
              return (
                <Card
                  onClick={() =>
                    department.name
                      ? setSelectedDepartments(
                          selectedDepartments.includes(department.name)
                            ? selectedDepartments.filter(name => name !== department.name)
                            : [...selectedDepartments, department.name],
                        )
                      : null
                  }
                  key={department.name}
                  backgroundColor='grey/dark/background'
                  style={{ cursor: 'pointer' }}
                >
                  <Box display='flex' flexDirection='row'>
                    <Box mt={'6px' as never} mr='s'>
                      <Checkbox
                        checked={
                          department.name ? selectedDepartments.includes(department.name) : false
                        }
                      />
                    </Box>
                    <Box flex='1' style={{ minWidth: 0 }}>
                      <Box
                        justifyContent='space-between'
                        display='flex'
                        alignItems='center'
                        flexDirection='row'
                      >
                        <Typography variant='h3'>{department.displayName}</Typography>
                        <Box display='flex' flexDirection='row' alignItems='center'>
                          <Svg name='users' size='s' />
                          <Typography ml='xxs' variant='h3'>
                            {department.employees?.length}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant='p3/light' mt='xs'>
                        {t('manager')}
                        {department.manager?.firstName || department.manager?.lastName ? (
                          <Typography variant='p2/light' as='span'>
                            {`${department.manager?.firstName || '-'} ${department.manager?.lastName || '-'}`}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </Typography>
                      <Typography variant='p3/light' ellipsis numberOfLines={1} mt='xxs'>
                        {t('team')}
                        <Typography variant='p2/light' as='span'>
                          {department.teams?.length ? department.teams?.join(', ') : '-'}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              );
            })}
        </Box>
      )}
      {selectedDepartments.length > 0 && (
        <AlertBadge
          type='white'
          containerProps={{ mt: 'm' }}
          showCta
          ctaLabel={t('buttons.viewPeople')}
          onClick={() => setStep(Steps.STEP_EMPLOYEES)}
          ctaProps={{
            variant: 'link/primary',
            color: 'secondary/background',
          }}
          label={
            <RenderLocalesWithComponents
              text={t('alert.info', {
                totalEmployees: departments
                  ?.filter(dept => (dept.name ? selectedDepartments.includes(dept.name) : false))
                  .reduce((acc, dept) => acc + (dept.employees ? dept.employees.length : 0), 0),
              })}
              placeholders={{
                '!{TOTAL_SELECTED_DEPARTMENTS}!': (
                  <Typography as='span' variant='p2/light' color='primary'>
                    {t('alert.selectedDeparments', {
                      totalDepartments: selectedDepartments.length,
                    })}
                  </Typography>
                ),
              }}
            />
          }
        />
      )}
      <Box justifyContent='flex-end' display='flex' flexDirection='row'>
        <Button onClick={() => setStep(Steps.STEP_CHOOSER)} mt='l' mr='xs' variant='link/primary'>
          {t('buttons.back')}
        </Button>
        <Button
          disabled={
            !!errorGetSurveyCampaignInvitationByTemplateId || selectedDepartments.length === 0
          }
          onClick={() => setStep(Steps.STEP_RECIPIENTS)}
          mt='l'
          variant='primary'
        >
          {t('buttons.continue')}
        </Button>
      </Box>
    </Box>
  );
};
